import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { apdmFailureReasonText } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmCampaignRecipientView,
  ApdmRecipientReportContext,
  ApdmRecipientReportMessage,
  RpaMessageType,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import dayjs from 'dayjs';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from 'lucide-react-native';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { ApdmTag } from '../components/ApdmSurface';
import { ConversationCouponCard } from './ConversationCouponCard';
import { apdmCustomerLook } from './campaignDetailText';

const kMessageTypeLabels: Partial<Record<RpaMessageType, string>> = {
  [RpaMessageType.Image]: '[图片]',
  [RpaMessageType.Video]: '[视频]',
  [RpaMessageType.Link]: '[链接]',
  [RpaMessageType.Voice]: '[语音]',
  [RpaMessageType.Miniprogram]: '[小程序]',
};

/** What a message without a picture to show says in its bubble. */
function messageText(message: ApdmRecipientReportMessage): string {
  if (message.text) return message.text;
  return (message.type && kMessageTypeLabels[message.type]) || '[消息]';
}

/** One customer's WeChat conversation with the shop, and their lead. */
export const ConversationSheet = memo(
  ({
    campaignId,
    recipient: open,
    onClose,
    onLead,
  }: {
    campaignId: string;
    recipient: ApdmCampaignRecipientView | null;
    onClose: () => void;
    onLead: (leadId: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { apdmCampaignService } = useServiceFactory();
    // The panel animates out after `open` clears; keep its content until
    // then.
    const [recipient, setRecipient] = useState(open);
    const [context, setContext] = useState<ApdmRecipientReportContext | null>(
      null,
    );
    const [failed, setFailed] = useState(false);
    useEffect(() => {
      if (!open) return;
      setRecipient(open);
      setContext(null);
      setFailed(false);
      let active = true;
      apdmCampaignService
        .recipientContext({ campaignId, recipientId: open.id })
        .then(result => {
          if (active) setContext(result);
        })
        .catch(error => {
          console.error('APDM recipient conversation failed', error);
          if (active) setFailed(true);
        });
      return () => {
        active = false;
      };
    }, [apdmCampaignService, campaignId, open]);
    const look = recipient ? apdmCustomerLook(recipient) : null;
    const preview = usePreviewImage();
    const card = context?.couponCard ?? null;
    // The coupon poster is drawn as its card, so it is not among the images
    // a tap pages through.
    const asCard = (message: ApdmRecipientReportMessage) =>
      !!card && !!message.couponPoster;
    const images = useMemo(
      () =>
        (context?.messages ?? []).flatMap(message =>
          message.imageUrl && !(context?.couponCard && message.couponPoster)
            ? [message.imageUrl]
            : [],
        ),
      [context],
    );
    // Without a name the plate is the title, and is not repeated below it.
    const customerName = recipient?.facts.customerName?.trim() || null;
    const name = customerName ?? recipient?.licensePlateNo;
    return (
      <BottomPanel visible={!!open} onClose={onClose} fitContent>
        {recipient && look ? (
          <View style={{ flexShrink: 1, backgroundColor: aiColors.background }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(10),
                paddingHorizontal: rpx(12),
                paddingVertical: rpx(12),
                backgroundColor: aiColors.card,
                borderBottomWidth: 1,
                borderBottomColor: aiColors.border,
              }}
            >
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="关闭"
                onPress={onClose}
                hitSlop={8}
              >
                <ChevronLeft size={22} color={aiColors.foreground} />
              </Pressable>
              <View
                style={{
                  width: rpx(40),
                  height: rpx(40),
                  borderRadius: rpx(20),
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: aiColors.secondary,
                }}
              >
                <Typography.Text
                  bold
                  size={rpx(15)}
                  color={aiColors.secondaryForeground}
                >
                  {name?.slice(0, 1)}
                </Typography.Text>
              </View>
              <View style={{ flex: 1, gap: rpx(2) }}>
                <Typography.Text
                  bold
                  size={rpx(15)}
                  color={aiColors.foreground}
                  numberOfLines={1}
                >
                  {name}
                </Typography.Text>
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                  numberOfLines={1}
                >
                  {[
                    customerName ? recipient.licensePlateNo : null,
                    recipient.facts.vehicleBrandModel,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography.Text>
              </View>
              <ApdmTag round text={look.label} color={look.color} />
            </View>
            {recipient.leadId ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => onLead(recipient.leadId!)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginHorizontal: rpx(16),
                  marginTop: rpx(12),
                  padding: rpx(12),
                  borderRadius: rpx(12),
                  backgroundColor: tint(aiColors.green, 0.08),
                }}
              >
                <Typography.Text
                  medium
                  size={rpx(13)}
                  color={aiColors.accentForeground}
                  style={{ flex: 1 }}
                >
                  这位客户有一条线索，查看跟进
                </Typography.Text>
                <ChevronRight size={16} color={aiColors.accentForeground} />
              </Pressable>
            ) : null}
            <ScrollView
              style={{ flexGrow: 0, flexShrink: 1 }}
              contentContainerStyle={{
                padding: rpx(16),
                paddingBottom: rpx(24),
                gap: rpx(14),
              }}
            >
              {failed ? (
                <Typography.Text
                  size={rpx(13)}
                  color={aiColors.mutedForeground}
                >
                  对话暂时读不出来，请稍后再试。
                </Typography.Text>
              ) : !context ? (
                <ActivityIndicator
                  color={aiColors.primary}
                  style={{ paddingVertical: rpx(24) }}
                />
              ) : context.messages.length === 0 ? (
                <Typography.Text
                  size={rpx(13)}
                  color={aiColors.mutedForeground}
                >
                  {recipient.failureReason
                    ? `还没发出去：${apdmFailureReasonText(
                        recipient.failureReason,
                      )}`
                    : '还没有聊天记录'}
                </Typography.Text>
              ) : (
                context.messages.map(message => {
                  const mine = message.sender !== 'customer';
                  return (
                    <View
                      key={message.id}
                      style={{
                        alignItems: mine ? 'flex-end' : 'flex-start',
                        gap: rpx(4),
                      }}
                    >
                      {asCard(message) ? (
                        <ConversationCouponCard coupon={card!} />
                      ) : message.imageUrl ? (
                        <TouchableOpacity
                          accessibilityRole="imagebutton"
                          accessibilityLabel="查看图片"
                          activeOpacity={0.9}
                          onPress={() =>
                            preview({
                              imageUrls: images,
                              index: images.indexOf(message.imageUrl!),
                            })
                          }
                          style={{
                            borderRadius: rpx(12),
                            overflow: 'hidden',
                            backgroundColor: aiColors.muted,
                          }}
                        >
                          <AdvancedImage
                            uri={message.imageUrl}
                            style={{ width: rpx(140), height: rpx(140) }}
                          />
                        </TouchableOpacity>
                      ) : (
                        <View
                          style={{
                            maxWidth: '80%',
                            paddingHorizontal: rpx(14),
                            paddingVertical: rpx(10),
                            borderRadius: rpx(18),
                            backgroundColor: mine
                              ? aiColors.primary
                              : aiColors.secondary,
                          }}
                        >
                          <Typography.Text
                            size={rpx(14)}
                            lineHeight={rpx(21)}
                            color={mine ? '#fff' : aiColors.foreground}
                          >
                            {messageText(message)}
                          </Typography.Text>
                        </View>
                      )}
                      <Typography.Text
                        size={rpx(11)}
                        color={aiColors.mutedForeground}
                      >
                        {dayjs(message.sentAt).format('M月D日 HH:mm')}
                      </Typography.Text>
                    </View>
                  );
                })
              )}
              {context?.coupons.length ? (
                <CouponList coupons={context.coupons} />
              ) : null}
            </ScrollView>
          </View>
        ) : null}
      </BottomPanel>
    );
  },
);

const CouponList = memo(
  ({ coupons }: { coupons: ApdmRecipientReportContext['coupons'] }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          marginTop: rpx(4),
          padding: rpx(14),
          gap: rpx(10),
          borderRadius: rpx(18),
          backgroundColor: aiColors.card,
          borderWidth: 1,
          borderColor: aiColors.border,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text bold size={rpx(14)} color={aiColors.foreground}>
            优惠券
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {coupons.length} 张
          </Typography.Text>
        </View>
        {coupons.map(coupon => {
          const state = couponStateLook(coupon);
          return (
            <View
              key={coupon.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(10),
                paddingHorizontal: rpx(12),
                paddingVertical: rpx(10),
                borderRadius: rpx(12),
                backgroundColor: aiColors.muted,
              }}
            >
              <View style={{ flex: 1, gap: rpx(2) }}>
                <Typography.Text
                  medium
                  size={rpx(14)}
                  color={aiColors.foreground}
                >
                  {coupon.label}
                </Typography.Text>
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                >
                  {coupon.redeemedAt
                    ? `${dayjs(coupon.redeemedAt).format('M月D日')} 已核销`
                    : state.label}
                </Typography.Text>
              </View>
              <ApdmTag
                round
                text={state.label}
                color={state.color}
                icon={state.icon}
              />
            </View>
          );
        })}
      </View>
    );
  },
);

function couponStateLook(
  coupon: ApdmRecipientReportContext['coupons'][number],
): { label: string; color: string; icon?: LucideIcon } {
  // A redeemed coupon is the outcome the campaign wanted.
  if (coupon.redeemedAt)
    return { label: '已核销', color: aiColors.green, icon: Check };
  if (coupon.status === 'expired')
    return { label: '已过期', color: aiColors.mutedForeground };
  if (coupon.status === 'revoked')
    return { label: '已作废', color: aiColors.mutedForeground };
  return { label: '未核销', color: aiColors.primary };
}
